/**
 * State and radio-service dropdown options, from the data actually imported.
 *
 * `/v1/licenses/facets` returns the distinct states, services and statuses present
 * in the FCC tables with counts, cached server-side and recomputed when an import
 * finishes. The license search page has used it since the client enabled all fifty
 * states; the CRM list pages now offer the same two filters, so they read the same
 * source rather than hard-coding a state list that would go stale the moment the
 * monitored set changes.
 *
 * Kept out of `components/FilterBar.tsx` so that file exports a component and
 * nothing else — see the note at the top of `utils/userOptions.ts`.
 */
import { useEffect, useState } from 'react'

import { getLicenseFacets } from '../services/licenses'
import type { FacetValue, LicenseFacets } from '../types'
import type { FilterOption } from '../components/FilterBar'

/**
 * Load the facet lists once per page.
 *
 * Failures are swallowed on purpose, the same way `useUserOptions` swallows its
 * own: an unreachable reference list must not break the page it sits on. The
 * dropdown falls back to its neutral option, which filters nothing.
 */
export function useLicenseFacets(): LicenseFacets | null {
  const [facets, setFacets] = useState<LicenseFacets | null>(null)
  useEffect(() => {
    getLicenseFacets()
      .then(setFacets)
      .catch(() => {})
  }, [])
  return facets
}

/**
 * Facet values as filter options, with the neutral option first.
 *
 * `selected` is added back if the data no longer contains it — a state that has
 * dropped out of the import since the filter was chosen must still be visible in the
 * control, or the page would silently show a filtered list with the control reading
 * "All states".
 */
export function facetFilterOptions(
  neutralLabel: string,
  values: FacetValue[] | undefined,
  selected: string,
  format: (v: FacetValue) => string = (v) => v.label,
): FilterOption[] {
  const options: FilterOption[] = [{ value: '', label: neutralLabel }]
  const list = values ?? []
  list.forEach((v) => options.push({ value: v.code, label: format(v) }))
  if (selected && !list.some((v) => v.code === selected)) {
    options.push({ value: selected, label: selected })
  }
  return options
}
